<script lang="ts">
	/**
	 * `/Checkout/Return` — where the provider sends the customer after paying (Design §6.2).
	 *
	 * **The return page must not be trusted.** The browser comes back before — sometimes well
	 * before — the webhook arrives, so this page grants nothing, concludes nothing and claims
	 * nothing. It polls `GET /api/checkout/session/:uuid` and renders whatever the Store says. That
	 * GET also performs the server's inline reconcile, which is what makes polling worth doing at
	 * all; the delayed reconcile jobs, not this page, are the guarantee that a customer who closes
	 * the tab still gets what they paid for.
	 *
	 * Four states, one per session status, because `expired` is reachable from both the §16 sweep
	 * and the provider's `checkout.expired` event and reads nothing like a failure to the customer.
	 * A fifth rendering hangs off `paid`: a `completed` session whose money has not arrived is a
	 * delayed-settlement method (boleto, SEPA, bank transfer, Konbini) and must not read as success
	 * — that is the visible half of the server's payment-status gate.
	 *
	 * The page is not in `AbstractID.publicRoutes`: the endpoint it lives on is `requireAuth`.
	 */

	import { onDestroy, onMount } from 'svelte';
	import { EmptyState, PageHeader, RoutedButton, RoutedLink } from '../../components';
	import { formatFriendlyDate } from '../../Utilities';
	import { getCheckoutSession } from '../../AppNetwork';
	import { flush, track } from '../../stores/analytics';
	import { configurationStore } from '../../AppConfiguration';
	import type { CheckoutSessionState } from '../../AppTypes';

	/**
	 * §6.2's cadence: 1s while the webhook is most likely to land, then 3s, and a hard stop at 120s
	 * into a terminal "we'll email you" — a promise the `checkout_pending` email template keeps.
	 * Polling forever would leave a tab hammering the reconcile path for a session that a human now
	 * has to look at.
	 */
	const FAST_INTERVAL_MS = 1_000;
	const SLOW_INTERVAL_MS = 3_000;
	const FAST_PHASE_MS = 10_000;
	const GIVE_UP_MS = 120_000;

	let sessionUUID: string = '';
	let state: CheckoutSessionState | null = null;
	/** Only shown while there is no state at all; a blip after one arrived must not blank the page. */
	let unreachableMessage: string = '';
	let gaveUp: boolean = false;

	let timer: ReturnType<typeof setTimeout> | null = null;
	let startedAt: number = 0;
	let stopped: boolean = false;

	$: supportEmail = $configurationStore?.supportEmail ?? '';
	$: productHref = state !== null && state.productSlug.length > 0 ? `/Product/${state.productSlug}` : '/';
	$: isProcessing = state !== null && (state.status === 'pending' || state.status === 'completing');

	/**
	 * How far along the purchase is, as the three things that actually happen (§6.2).
	 *
	 * A bare spinner said "wait" without saying what for, and the wait here is genuinely
	 * two-staged: the browser is back from the provider long before the webhook is, and on a
	 * delayed-settlement method it may be days early. Naming the steps is what makes a slow one
	 * read as progress rather than as a stall — and it is the same three steps in the terminal
	 * states, so the page a customer lands on is the page they were watching.
	 */
	$: steps =
		state === null
			? []
			: [
					{ label: 'Payment submitted', done: true },
					{
						label: state.paid ? 'Payment confirmed' : 'Confirming payment',
						done: state.paid,
						// `completed` without `paid` is not stalled, it is waiting on a bank — the
						// distinction the whole payment-status gate exists to preserve.
						waiting: !state.paid && state.status !== 'failed' && state.status !== 'expired'
					},
					{
						label: 'Access set up',
						done: state.status === 'completed' && state.paid,
						waiting: state.status !== 'failed' && state.status !== 'expired'
					}
			  ];

	/** Where the purchase now lives, so success is a door rather than a full stop. */
	$: subscriptionHref =
		state !== null && state.subscriptionUUID !== null
			? `/Account/Subscription/${state.subscriptionUUID}`
			: null;
	$: invoiceHref = state !== null && state.invoiceUUID !== null ? `/Account/Invoices` : null;

	function fnStopPolling(): void {
		stopped = true;
		if (timer !== null) {
			clearTimeout(timer);
			timer = null;
		}
	}

	async function fnPoll(): Promise<void> {
		if (stopped) return;

		try {
			state = await getCheckoutSession(sessionUUID);
			unreachableMessage = '';
		} catch (error) {
			// Keep the last known state if there is one: a purchase that has already been reported
			// completed must not turn into an error page because one poll timed out.
			if (state === null) {
				unreachableMessage = (error as Error).message;
				fnStopPolling();
				return;
			}
		}

		if (stopped) return;

		// `pending` and `completing` are the only statuses that can still move.
		if (state !== null && state.status !== 'pending' && state.status !== 'completing') {
			/*
			 * §12.2, at the moment the session actually settles rather than on arrival.
			 *
			 * `paid` travels beside the status because they are different facts: a delayed-settlement
			 * method completes the session days before the money arrives, and a funnel counting the
			 * completion as a sale would report revenue the store has not received. The **server**
			 * writes the purchased stage from the completion path; this is only what the customer
			 * saw.
			 */
			track('checkout_returned', {
				path: '/Checkout/Return',
				properties: { status: state.status, paid: state.paid }
			});
			// Flushed rather than queued: this is routinely the last thing that happens in a visit.
			void flush(true);
			fnStopPolling();
			return;
		}

		const elapsed = Date.now() - startedAt;
		if (elapsed >= GIVE_UP_MS) {
			gaveUp = true;
			fnStopPolling();
			return;
		}

		// Clamped to the deadline so the last poll lands **on** 120s rather than three seconds past
		// it — the give-up is only ever evaluated by a poll.
		const interval = elapsed < FAST_PHASE_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS;
		timer = setTimeout(() => void fnPoll(), Math.min(interval, GIVE_UP_MS - elapsed));
	}

	function fnRetry(): void {
		stopped = false;
		gaveUp = false;
		unreachableMessage = '';
		startedAt = Date.now();
		void fnPoll();
	}

	onMount(() => {
		sessionUUID = new URLSearchParams(window.location.search).get('session') ?? '';

		// Nothing to ask about. Polling an empty uuid would 404 once a second for two minutes.
		if (sessionUUID.length === 0) {
			unreachableMessage = 'This page was opened without a checkout session.';
			stopped = true;
			return;
		}

		startedAt = Date.now();
		void fnPoll();
	});

	onDestroy(fnStopPolling);
</script>

<PageHeader title="Your purchase" />

<!--
	The region rewrites itself with no user action — the customer is watching a payment settle — so
	a screen reader has to be told it changed. `polite` rather than `assertive`: this is a status,
	not an interruption.
-->
<div aria-live="polite" aria-busy={isProcessing && !gaveUp}>
	{#if unreachableMessage.length > 0 && state === null}
		<EmptyState
			variant="error"
			title="We could not check your purchase."
			description={unreachableMessage}
		>
			{#if sessionUUID.length > 0}
				<RoutedButton href="/Account" color="primary" size="sm">Go to your account</RoutedButton>
			{:else}
				<RoutedButton href="/" color="primary" size="sm">Back to the store</RoutedButton>
			{/if}
		</EmptyState>
	{:else if state === null}
		<EmptyState variant="loading" title="Checking your purchase…" />
	{:else if gaveUp && isProcessing}
		<!-- The 120s stop. The purchase is not lost; it is simply no longer worth watching here. -->
		<EmptyState
			faIcon="envelope-circle-check"
			title="This is taking longer than usual."
			description="Your payment is still being processed. Nothing is lost. We'll email you as soon as it completes, and it will appear in your account."
		>
			<RoutedButton href="/Account" color="primary" size="sm">Go to your account</RoutedButton>
		</EmptyState>
	{:else if isProcessing}
		<div class="widget bg-depth shadow-widget max-w-xl mx-auto space-y-4">
			<div class="flex items-start gap-3">
				<i class="fa-solid fa-circle-notch fa-spin text-primary-500 text-xl mt-1" />
				<div>
					<h2 class="widget-title !mb-1">Confirming your payment…</h2>
					<p class="text-sm text-default">
						This usually takes a few seconds. You can leave this page, and your purchase completes
						either way.
					</p>
				</div>
			</div>
			<!--
				Named steps rather than a spinner: the wait is genuinely two-staged, and a customer
				who can see which stage is outstanding reads a slow one as progress.
			-->
			<ol class="space-y-2">
				{#each steps as step (step.label)}
					<li class="flex items-center gap-3 text-sm">
						{#if step.done}
							<i class="fa-solid fa-circle-check text-green-600 dark:text-green-400" />
							<span>{step.label}</span>
						{:else}
							<i class="fa-regular fa-circle text-default" />
							<span class="text-default">{step.label}</span>
						{/if}
					</li>
				{/each}
			</ol>
		</div>
	{:else if state.status === 'completed' && state.paid}
		<div class="widget bg-depth shadow-widget max-w-xl mx-auto space-y-5">
			<div class="text-center space-y-3">
				<span
					class="inline-flex w-14 h-14 rounded-full bg-green-100 dark:bg-green-500/15 items-center justify-center"
				>
					<i class="fa-solid fa-check text-green-600 dark:text-green-400 text-2xl" />
				</span>
				<h2 class="widget-title !mb-0">Thank you. {state.productName} is yours.</h2>
				<p class="text-sm text-default">
					Your payment went through and your access has been set up. A receipt is on its way by
					email.
				</p>
			</div>

			<!--
				What the product (or its family) says to do next — the documentation, the licence
				portal, a download — written by whoever authored the catalogue. First after the
				confirmation, because it is the most specific thing on the page: the generic doors
				below are the same for every purchase.
			-->
			{#if state.nextSteps}
				<div class="widget bg-depth space-y-3" data-testid="next-steps">
					{#if state.nextSteps.message}
						<p class="text-sm whitespace-pre-line">{state.nextSteps.message}</p>
					{/if}
					{#if state.nextSteps.links.length > 0}
						<div class="flex flex-wrap gap-2">
							{#each state.nextSteps.links as link, index (index)}
								{#if link.url.startsWith('/')}
									<RoutedButton href={link.url} color={index === 0 ? 'primary' : 'alternative'} size="sm">
										{link.label}
									</RoutedButton>
								{:else}
									<!-- Leaves the store, so it says so and opens beside it. -->
									<a
										href={link.url}
										target="_blank"
										rel="noopener noreferrer"
										class="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium {index === 0
											? 'bg-primary-600 text-white hover:bg-primary-700'
											: 'border border-gray-300 dark:border-zinc-600 hover:bg-gray-100 dark:hover:bg-zinc-700'}"
									>
										{link.label}
										<i class="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
									</a>
								{/if}
							{/each}
						</div>
					{/if}
				</div>
			{/if}

			<!--
				What was actually bought, stated back. The page used to say only the product name, so
				the one screen that confirms a purchase carried less detail than the email about it.
			-->
			<dl class="widget bg-depth text-sm space-y-2">
				<div class="flex items-baseline justify-between gap-4">
					<dt class="text-default">Product</dt>
					<dd class="font-medium text-right">{state.productName}</dd>
				</div>
				{#if state.completedAt !== null}
					<div class="flex items-baseline justify-between gap-4">
						<dt class="text-default">Completed</dt>
						<dd class="text-right">{formatFriendlyDate(state.completedAt)}</dd>
					</div>
				{/if}
				<div class="flex items-baseline justify-between gap-4">
					<dt class="text-default">Reference</dt>
					<dd class="font-mono text-xs text-right break-all">{state.uuid}</dd>
				</div>
			</dl>

			<!--
				Three doors rather than one. "Go to your account" is the safe default, but a customer
				who has just bought a subscription almost always wants the subscription itself, and a
				finance team wants the invoice — both were a hunt from here.
			-->
			<div class="flex flex-wrap items-center justify-center gap-3">
				<RoutedButton href="/Account" color="primary" size="sm">Go to your account</RoutedButton>
				{#if subscriptionHref !== null}
					<RoutedButton href={subscriptionHref} color="alternative" size="sm">
						Manage this subscription
					</RoutedButton>
				{/if}
				{#if invoiceHref !== null}
					<RoutedLink to={invoiceHref} class="text-sm text-primary-500 hover:underline">
						View your invoices
					</RoutedLink>
				{/if}
			</div>
		</div>
	{:else if state.status === 'completed'}
		<!--
			`completed` means the *session* finished, not that money arrived. Announcing success here
			is how a perpetual entitlement gets issued against a payment that never lands.
		-->
		<div class="widget bg-depth shadow-widget max-w-xl mx-auto space-y-5">
			<div class="text-center space-y-3">
				<span
					class="inline-flex w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-500/15 items-center justify-center"
				>
					<i class="fa-solid fa-hourglass-half text-amber-600 dark:text-amber-400 text-xl" />
				</span>
				<h2 class="widget-title !mb-0">Your payment is still being confirmed.</h2>
				<p class="text-sm text-default">
					Some payment methods (bank transfers and direct debits among them) take a few days to
					settle. Nothing more is needed from you: we'll email you the moment {state.productName} is
					ready, and it will appear in your account then.
				</p>
			</div>

			<!-- The same three steps, so the page they land on is the page they were watching. -->
			<ol class="widget bg-depth space-y-2">
				{#each steps as step (step.label)}
					<li class="flex items-center gap-3 text-sm">
						{#if step.done}
							<i class="fa-solid fa-circle-check text-green-600 dark:text-green-400" />
							<span>{step.label}</span>
						{:else}
							<i class="fa-regular fa-clock text-default" />
							<span class="text-default">{step.label}</span>
						{/if}
					</li>
				{/each}
			</ol>

			<div class="flex justify-center">
				<RoutedButton href="/Account" color="primary" size="sm">Go to your account</RoutedButton>
			</div>
		</div>
	{:else if state.status === 'expired'}
		<EmptyState
			faIcon="hourglass-end"
			title="This checkout expired."
			description="The payment page was left open too long, so it was closed. Nothing was charged, so start the purchase again when you're ready."
		>
			<RoutedButton href={productHref} color="primary" size="sm">Start again</RoutedButton>
		</EmptyState>
	{:else}
		<EmptyState
			variant="error"
			title="Your payment did not go through."
			description="Nothing was charged. Your bank or card issuer may be able to say why."
		>
			<RoutedButton href={productHref} color="primary" size="sm">Try again</RoutedButton>
			{#if supportEmail.length > 0}
				<a class="text-sm text-primary-500 hover:underline" href={`mailto:${supportEmail}`}>
					Contact support
				</a>
			{/if}
		</EmptyState>
	{/if}
</div>

{#if unreachableMessage.length > 0 && state === null && sessionUUID.length > 0}
	<div class="flex justify-center">
		<button class="text-sm text-primary-500 hover:underline" on:click={fnRetry}>Check again</button>
	</div>
{/if}
